import React, { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';

/**
 * ShopFieldRatioModal - Modal for changing the shop/field ratio globally
 * 
 * This affects all tracker_piping records AND all history records for the job.
 * Used for presentation demos to show how ratio changes affect earned hours calculations.
 */
const ShopFieldRatioModal = ({ isOpen, onClose, jobNumber, onSuccess }) => {
  const { instance, accounts } = useMsal();
  const [ratio, setRatio] = useState(60);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState('');
  const [stats, setStats] = useState({ trackerRecordCount: 0, historyRecordCount: 0 });

  // Fetch current ratio when modal opens
  useEffect(() => {
    if (isOpen && jobNumber) {
      fetchCurrentRatio();
    }
  }, [isOpen, jobNumber]);

  const fetchCurrentRatio = async () => {
    setFetching(true);
    setError('');
    try {
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'GET',
        `/matrix/shop-field-ratio/${jobNumber}`
      );
      if (response.data.success) {
        setRatio(response.data.shopFieldRatio);
        setStats({
          trackerRecordCount: response.data.trackerRecordCount,
          historyRecordCount: response.data.historyRecordCount
        });
      }
    } catch (err) {
      console.error('Error fetching shop/field ratio:', err);
      setError('Failed to fetch current ratio');
    } finally {
      setFetching(false);
    }
  };

  const handleSubmit = async () => {
    if (!confirmed) {
      setError('Please confirm you understand the impact of this change');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'PUT',
        `/matrix/shop-field-ratio/${jobNumber}`,
        { ratio }
      );
      if (response.data.success) {
        onSuccess && onSuccess(response.data);
        onClose();
      } else {
        setError(response.data.message || 'Failed to update ratio');
      }
    } catch (err) {
      console.error('Error updating shop/field ratio:', err);
      setError(err.response?.data?.message || 'Failed to update ratio');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setConfirmed(false);
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div style={overlayStyle}>
      <div style={modalStyle}>
        {/* Header */}
        <div style={headerStyle}>
          <h2 style={{ margin: 0, fontSize: '1.25rem' }}>⚙️ Shop/Field Ratio Configuration</h2>
          <button onClick={handleClose} style={closeButtonStyle}>×</button>
        </div>

        {/* Content */}
        <div style={contentStyle}>
          {fetching ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <p>Loading current ratio...</p>
            </div>
          ) : (
            <>
              {/* Current Stats */}
              <div style={statsBoxStyle}>
                <div style={statItemStyle}>
                  <span style={statLabelStyle}>Job Number:</span>
                  <span style={statValueStyle}>{jobNumber}</span>
                </div>
                <div style={statItemStyle}>
                  <span style={statLabelStyle}>Tracker Records:</span>
                  <span style={statValueStyle}>{stats.trackerRecordCount}</span>
                </div>
                <div style={statItemStyle}>
                  <span style={statLabelStyle}>History Records:</span>
                  <span style={statValueStyle}>{stats.historyRecordCount}</span>
                </div>
              </div>

              {/* Ratio Input */}
              <div style={inputGroupStyle}>
                <label style={labelStyle}>Shop/Field Ratio (%)</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={ratio}
                    onChange={(e) => setRatio(parseFloat(e.target.value))}
                    style={{ flex: 1 }}
                  />
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.5"
                    value={ratio}
                    onChange={(e) => setRatio(Math.min(100, Math.max(0, parseFloat(e.target.value) || 0)))}
                    style={numberInputStyle}
                  />
                  <span>%</span>
                </div>
                <p style={helpTextStyle}>
                  Shop hours = Budget Hours × {ratio}% | Field hours = Budget Hours × {100 - ratio}%
                </p>
              </div>

              {/* Warning Box */}
              <div style={warningBoxStyle}>
                <p style={{ margin: '0 0 0.5rem 0', fontWeight: 'bold', color: '#856404' }}>
                  ⚠️ WARNING: This action will impact historical data!
                </p>
                <ul style={{ margin: '0.5rem 0', paddingLeft: '1.25rem' }}>
                  <li>ALL {stats.trackerRecordCount} records in tracker_piping will be updated</li>
                  <li>ALL {stats.historyRecordCount} records in tracker_piping_history will be updated</li>
                  <li>Earned hours calculations will be recalculated</li>
                  <li>Progress curves and reports will show different values</li>
                </ul>
              </div>

              {/* Confirmation Checkbox */}
              <label style={checkboxLabelStyle}>
                <input
                  type="checkbox"
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                  style={{ marginRight: '0.5rem' }}
                />
                I understand this will permanently change historical data and affect all reports
              </label>

              {/* Error Message */}
              {error && (
                <div style={errorBoxStyle}>
                  {error}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div style={footerStyle}>
          <button onClick={handleClose} style={cancelButtonStyle}>
            Cancel
          </button>
          <button 
            onClick={handleSubmit} 
            disabled={loading || fetching || !confirmed}
            style={{
              ...applyButtonStyle,
              opacity: (loading || fetching || !confirmed) ? 0.5 : 1,
              cursor: (loading || fetching || !confirmed) ? 'not-allowed' : 'pointer'
            }}
          >
            {loading ? 'Applying...' : 'Apply Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Styles
const overlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 10000
};

const modalStyle = {
  backgroundColor: 'white',
  borderRadius: '8px',
  width: '500px',
  maxWidth: '90vw',
  maxHeight: '90vh',
  overflow: 'hidden',
  boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
};

const headerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '1rem 1.5rem',
  borderBottom: '1px solid #ddd',
  backgroundColor: '#f8f9fa'
};

const closeButtonStyle = {
  background: 'none',
  border: 'none',
  fontSize: '1.5rem',
  cursor: 'pointer',
  color: '#666',
  padding: '0.25rem 0.5rem',
  lineHeight: 1
};

const contentStyle = {
  padding: '1.5rem'
};

const statsBoxStyle = {
  backgroundColor: '#e8f4fd',
  borderRadius: '6px',
  padding: '1rem',
  marginBottom: '1rem'
};

const statItemStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  marginBottom: '0.25rem'
};

const statLabelStyle = {
  color: '#666'
};

const statValueStyle = {
  fontWeight: 'bold',
  color: '#333'
};

const inputGroupStyle = {
  marginBottom: '1rem'
};

const labelStyle = {
  display: 'block',
  fontWeight: 'bold',
  marginBottom: '0.5rem'
};

const numberInputStyle = {
  width: '70px',
  padding: '0.5rem',
  border: '1px solid #ccc',
  borderRadius: '4px',
  textAlign: 'center',
  fontSize: '1rem'
};

const helpTextStyle = {
  margin: '0.5rem 0 0 0',
  fontSize: '0.85rem',
  color: '#666'
};

const warningBoxStyle = {
  backgroundColor: '#fff3cd',
  border: '1px solid #ffc107',
  borderRadius: '6px',
  padding: '1rem',
  marginBottom: '1rem'
};

const checkboxLabelStyle = {
  display: 'flex',
  alignItems: 'flex-start',
  cursor: 'pointer',
  fontSize: '0.9rem',
  color: '#333'
};

const errorBoxStyle = {
  backgroundColor: '#f8d7da',
  border: '1px solid #f5c6cb',
  borderRadius: '4px',
  padding: '0.75rem 1rem',
  marginTop: '1rem',
  color: '#721c24'
};

const footerStyle = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '0.75rem',
  padding: '1rem 1.5rem',
  borderTop: '1px solid #ddd',
  backgroundColor: '#f8f9fa'
};

const cancelButtonStyle = {
  padding: '0.5rem 1.5rem',
  border: '1px solid #ccc',
  borderRadius: '4px',
  backgroundColor: 'white',
  cursor: 'pointer',
  fontSize: '0.9rem'
};

const applyButtonStyle = {
  padding: '0.5rem 1.5rem',
  border: 'none',
  borderRadius: '4px',
  backgroundColor: '#007bff',
  color: 'white',
  cursor: 'pointer',
  fontSize: '0.9rem',
  fontWeight: 'bold'
};

export default ShopFieldRatioModal;

